import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { ArrowRight, Instagram, MessageCircle, TrendingUp } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";

const SITE = "https://kentro.com.br";
const URL = `${SITE}/calculadora-whatsapp-outubro-2026`;

const TITLE = "Calculadora WhatsApp API — Cobrança Outubro/2026 | Kentro";
const DESC =
  "Descubra quanto sua empresa passará a pagar pela API Oficial do WhatsApp com as novas tarifas da Meta a partir de 1º/10/2026.";

const PRECO_UTILIDADE = 0.03;
const PRECO_MARKETING = 0.30;
const PRECO_MSG_SERVICO_OUT = 0.03;

const searchSchema = z.object({
  u: z.coerce.number().int().min(0).default(0),
  m: z.coerce.number().int().min(0).default(0),
  p: z.coerce.number().int().min(0).default(0),
  s: z.coerce.number().int().min(0).default(0),
  e: z.string().optional(),
});

export const Route = createFileRoute("/calculadora-whatsapp-outubro-2026")({
  validateSearch: (search) => {
    const r = searchSchema.safeParse(search);
    return r.success ? r.data : searchSchema.parse({});
  },
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:locale", content: "pt_BR" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: ResultadoPage,
});

const fmt = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

function ResultadoPage() {
  const { u, m, p, s } = Route.useSearch();
  const headingRef = useRef<HTMLHeadingElement>(null);

  const custoHoje = u * PRECO_UTILIDADE + m * PRECO_MARKETING;
  const custoOutubro = custoHoje + s * PRECO_MSG_SERVICO_OUT;
  const aumentoMensal = custoOutubro - custoHoje;
  const aumentoAnual = aumentoMensal * 12;
  const aumentoPct = custoHoje > 0 ? (aumentoMensal / custoHoje) * 100 : null;

  const maxVal = Math.max(custoHoje, custoOutubro, 1);
  const barHoje = (custoHoje / maxVal) * 100;
  const barOut = (custoOutubro / maxVal) * 100;

  const hasData = u + m + p + s > 0;

  const trackingPayload = {
    utility_month: u,
    marketing_month: m,
    passive_month: p,
    service_month: s,
    custo_hoje: Number(custoHoje.toFixed(2)),
    custo_outubro: Number(custoOutubro.toFixed(2)),
    aumento_mensal: Number(aumentoMensal.toFixed(2)),
    aumento_anual: Number(aumentoAnual.toFixed(2)),
    currency: "BRL",
  };

  useEffect(() => {
    if (!hasData) return;
    trackEvent("whatsapp_calc_result_view", trackingPayload);
    // Move o foco para o título do resultado para leitores de tela e teclado
    headingRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasData, u, m, p, s]);

  return (
    <>
      <section className="gradient-hero relative overflow-hidden pt-24 pb-10">
        <div className="circuit-pattern absolute inset-0 opacity-30" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary-glow">
            <TrendingUp className="size-3.5" /> Resultado da simulação
          </span>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="mt-4 text-3xl font-bold tracking-tight outline-none focus-visible:ring-2 focus-visible:ring-primary-glow focus-visible:ring-offset-2 focus-visible:ring-offset-background md:text-5xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Seu custo do{" "}
            <span className="gradient-text">WhatsApp em outubro/2026</span>
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
            Comparativo mensal com as tarifas atuais da Meta para o Brasil, considerando a
            nova cobrança de mensagens de serviço a partir de 1º/10/2026.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16" aria-labelledby={undefined}>
        <div
          className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8"
          role="region"
          aria-live="polite"
          aria-atomic="false"
          aria-label="Resultado da simulação de custo do WhatsApp"
        >
          {!hasData ? (
            <div className="glass-card rounded-3xl p-8 text-center">
              <p className="text-muted-foreground">
                Nenhum dado recebido. Volte para a calculadora e preencha os campos.
              </p>
              <Button asChild className="mt-4">
                <Link to="/" hash="calculadora">
                  Voltar para a calculadora
                </Link>
              </Button>
            </div>
          ) : (
            <>
              <div className="glass-card rounded-3xl p-6 sm:p-8">
                <h2 className="text-xl font-bold sm:text-2xl" style={{ fontFamily: "var(--font-display)" }}>
                  Como chegamos nesse resultado
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Veja abaixo a conta mensal com as tarifas da Meta para o Brasil.
                </p>

                <div className="mt-5 space-y-3">
                  <CalcLine
                    label="Mensagens de utilidade"
                    qty={u}
                    unitPrice={PRECO_UTILIDADE}
                    total={u * PRECO_UTILIDADE}
                  />
                  <CalcLine
                    label="Mensagens de marketing"
                    qty={m}
                    unitPrice={PRECO_MARKETING}
                    total={m * PRECO_MARKETING}
                  />

                  <div className="flex items-center justify-between border-t border-white/10 pt-3 text-sm">
                    <span className="font-medium text-foreground/90">Custo hoje</span>
                    <span className="font-semibold text-[var(--mint)]">{fmt.format(custoHoje)}/mês</span>
                  </div>

                  <CalcLine
                    label="Mensagens de serviço cobradas em out/2026"
                    qty={s}
                    unitPrice={PRECO_MSG_SERVICO_OUT}
                    total={s * PRECO_MSG_SERVICO_OUT}
                    variant="new"
                  />

                  <div className="flex items-center justify-between border-t border-destructive/20 pt-3">
                    <span className="font-semibold text-foreground/90">Custo a partir de out/2026</span>
                    <span className="text-xl font-bold text-destructive" style={{ fontFamily: "var(--font-display)" }}>
                      {fmt.format(custoOutubro)}/mês
                    </span>
                  </div>

                  <div className="flex items-center justify-between rounded-xl bg-destructive/10 p-3">
                    <span className="font-semibold text-foreground/90">Aumento mensal</span>
                    <span className="text-lg font-bold text-destructive" style={{ fontFamily: "var(--font-display)" }}>
                      +{fmt.format(aumentoMensal)}/mês
                    </span>
                  </div>
                </div>

                {p > 0 && (
                  <p className="mt-4 text-xs text-muted-foreground">
                    <strong>{p.toLocaleString("pt-BR")} atendimentos passivos</strong> não entram no custo porque o cliente iniciou a conversa — essa janela continua gratuita hoje.
                  </p>
                )}
              </div>

              <div className="glass-card mt-6 rounded-3xl p-6 sm:p-8">
                <h2 className="text-xl font-bold sm:text-2xl" style={{ fontFamily: "var(--font-display)" }}>
                  O que isso significa
                </h2>
                <p className="mt-3 text-foreground/85">
                  As mensagens que sua empresa envia no chat vão deixar de ser gratuitas.
                  Quem depende só do WhatsApp para marketing e atendimento vai sentir no
                  bolso. A saída é diversificar a comunicação — usando também canais como
                  o <strong>Telegram</strong>, que não cobra por mensagem — sem abandonar
                  o WhatsApp. Com a Kentro, sua empresa atende WhatsApp, Telegram,
                  Instagram e e-mail em um só lugar, com IA e automações, e reduz a
                  dependência de um único canal.
                </p>

                <div className="mt-6 flex flex-wrap gap-3">
                  <Button asChild size="lg" className="gradient-primary text-white shadow-glow">
                    <a
                      href="https://chat.whatsapp.com/DsTFhZoVb3B9wBWn5icW4p"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        trackEvent("whatsapp_calc_cta_click", {
                          cta: "comunidade_whatsapp",
                          ...trackingPayload,
                        })
                      }
                    >
                      <MessageCircle className="size-4" />
                      Entrar na comunidade da Kentro no WhatsApp
                      <ArrowRight className="size-4" />
                    </a>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <a
                      href="https://instagram.com/kentrosistemas"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        trackEvent("whatsapp_calc_cta_click", {
                          cta: "instagram_kentro",
                          ...trackingPayload,
                        })
                      }
                    >
                      <Instagram className="size-4" />
                      Seguir a Kentro no Instagram
                    </a>
                  </Button>
                </div>

                <p className="mt-6 text-xs text-muted-foreground">
                  Estimativa com base nas tarifas da Meta para o Brasil. A Meta publicará
                  as tarifas definitivas de outubro/2026 até 1º/09/2026.
                </p>
              </div>

              <div className="glass-card mt-6 animate-in fade-in slide-in-from-bottom-4 rounded-3xl p-6 duration-500 sm:p-10">
                <div className="grid gap-6 md:grid-cols-2">
                  <ComparativeCard
                    label="Hoje"
                    value={fmt.format(custoHoje)}
                    barPct={barHoje}
                    tone="ok"
                  />
                  <ComparativeCard
                    label="A partir de out/2026"
                    value={fmt.format(custoOutubro)}
                    barPct={barOut}
                    tone="alert"
                  />
                </div>

                <div className="mt-8">
                  <h3
                    className="mb-4 text-lg font-semibold"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    Comparativo de custos por categoria
                  </h3>
                  <div className="overflow-hidden rounded-2xl border border-white/10">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-white/[0.05] text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <tr>
                          <th className="px-3 py-3 sm:px-6">Categoria</th>
                          <th className="px-3 py-3 text-right sm:px-6">Hoje</th>
                          <th className="px-3 py-3 text-right sm:px-6">Out/2026</th>
                          <th className="px-3 py-3 text-right sm:px-6">Diferença</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/10">
                        <ComparisonRow
                          label="Mensagens de utilidade"
                          today={u * PRECO_UTILIDADE}
                          october={u * PRECO_UTILIDADE}
                        />
                        <ComparisonRow
                          label="Mensagens de marketing"
                          today={m * PRECO_MARKETING}
                          october={m * PRECO_MARKETING}
                        />
                        <ComparisonRow
                          label="Mensagens de serviço"
                          today={0}
                          october={s * PRECO_MSG_SERVICO_OUT}
                          isNew
                        />
                        <tr className="bg-white/[0.03] font-semibold">
                          <td className="px-3 py-3 sm:px-6">Total mensal</td>
                          <td className="px-3 py-3 text-right text-[var(--mint)] sm:px-6">
                            {fmt.format(custoHoje)}/mês
                          </td>
                          <td className="px-3 py-3 text-right text-destructive sm:px-6">
                            {fmt.format(custoOutubro)}/mês
                          </td>
                          <td className="px-3 py-3 text-right text-destructive sm:px-6">
                            +{fmt.format(aumentoMensal)}/mês
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mt-8 rounded-2xl border border-destructive/30 bg-destructive/10 p-5 text-center">
                  <div className="text-xs font-semibold uppercase tracking-wider text-destructive">
                    Aumento estimado
                  </div>
                  <div
                    className="mt-2 text-3xl font-bold text-destructive sm:text-4xl"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    +{fmt.format(aumentoMensal)}/mês
                    {aumentoPct !== null && (
                      <span className="ml-2 text-xl font-semibold sm:text-2xl">
                        ({aumentoPct.toFixed(0)}%)
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-foreground/80">
                    Impacto anual: <strong>+{fmt.format(aumentoAnual)}/ano</strong>
                  </div>
                  {custoHoje === 0 && custoOutubro > 0 && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      De {fmt.format(0)} para {fmt.format(custoOutubro)}/mês.
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 text-center">
                <Link
                  to="/"
                  hash="calculadora"
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  ← Refazer a simulação
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}

function CalcLine({
  label,
  qty,
  unitPrice,
  total,
  variant = "current",
}: {
  label: string;
  qty: number;
  unitPrice: number;
  total: number;
  variant?: "current" | "new";
}) {
  if (qty === 0) return null;
  const unitFmt = fmt.format(unitPrice);
  const totalFmt = fmt.format(total);
  const isNew = variant === "new";
  return (
    <div className="flex items-center justify-between text-sm">
      <div className="flex flex-col gap-0.5">
        <span className="font-medium text-foreground/90">{label}</span>
        <span className="text-xs text-muted-foreground">
          {qty.toLocaleString("pt-BR")} msg × {unitFmt}
        </span>
      </div>
      <span className={`font-semibold ${isNew ? "text-destructive" : "text-foreground/90"}`}>
        {totalFmt}
      </span>
    </div>
  );
}

function ComparisonRow({
  label,
  today,
  october,
  isNew = false,
}: {
  label: string;
  today: number;
  october: number;
  isNew?: boolean;
}) {
  if (today === 0 && october === 0) return null;
  const diff = october - today;
  const diffClass =
    diff > 0 ? "text-destructive" : diff < 0 ? "text-[var(--mint)]" : "text-muted-foreground";
  return (
    <tr>
      <td className="px-3 py-3 sm:px-6">
        {label}
        {isNew && (
          <span className="ml-2 rounded bg-destructive/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-destructive">
            Novo
          </span>
        )}
      </td>
      <td className="px-3 py-3 text-right tabular-nums sm:px-6">{fmt.format(today)}/mês</td>
      <td className="px-3 py-3 text-right tabular-nums sm:px-6">
        {fmt.format(october)}/mês
      </td>
      <td className={`px-3 py-3 text-right tabular-nums sm:px-6 ${diffClass}`}>
        {diff > 0 ? `+${fmt.format(diff)}` : fmt.format(diff)}/mês
      </td>
    </tr>
  );
}

function ComparativeCard({
  label,
  value,
  barPct,
  tone,
}: {
  label: string;
  value: string;
  barPct: number;
  tone: "ok" | "alert";
}) {
  const barColor = tone === "ok" ? "bg-[var(--mint)]" : "bg-destructive";
  const textColor = tone === "ok" ? "text-[var(--mint)]" : "text-destructive";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div
        className={`mt-2 text-3xl font-bold sm:text-4xl ${textColor}`}
        style={{ fontFamily: "var(--font-display)" }}
      >
        {value}
        <span className="ml-1 text-sm font-medium text-muted-foreground">/mês</span>
      </div>
      <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-white/10">
        <div
          className={`h-full ${barColor} transition-all duration-700`}
          style={{ width: `${Math.max(4, barPct)}%` }}
        />
      </div>
    </div>
  );
}
